export const API_URL = "https://urbanpodcasts-backend-api.azurewebsites.net";
const REMEMBER_2FA_TOKEN_KEY = "remember_2fa_token";

/* ============================================================
   TOKEN + AUTH HANDLING
============================================================ */

function getToken() {
    return localStorage.getItem("token");
}

export function getRememberedTwoFactorToken() {
    return String(localStorage.getItem(REMEMBER_2FA_TOKEN_KEY) || "").trim();
}

export function setRememberedTwoFactorToken(token) {
    const value = String(token || "").trim();
    if (!value) {
        localStorage.removeItem(REMEMBER_2FA_TOKEN_KEY);
        return;
    }
    localStorage.setItem(REMEMBER_2FA_TOKEN_KEY, value);
}

function handleAuthFailure() {
    localStorage.removeItem("token");
    window.location.href = "/"; // Redirect to login
}

/**
 * apiFetch — wrapper around fetch:
 *  - Automatically includes Authorization header
 *  - Supports JSON and FormData
 *  - Auto-handles expired tokens (401/403)
 */
async function apiFetch(path, options = {}) {
    const token = getToken();

    const headers = { ...(options.headers || {}) };

    // If sending JSON, set content-type (NOT for FormData)
    if (!(options.body instanceof FormData)) {
        headers["Content-Type"] = headers["Content-Type"] || "application/json";
    }

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(`${API_URL}${path}`, {
        ...options,
        headers,
    });

    // Try decoding JSON
    const data = await res.json().catch(() => ({}));

    // ----- Auto logout on expired token -----
    if (res.status === 401 || data?.message === "Invalid or expired token") {
        handleAuthFailure();
        return { ok: false, message: "Invalid or expired token" };
    }

    return data;
}

/**
 * apiUpload — wrapper around XMLHttpRequest for FormData uploads:
 *  - Includes Authorization header
 *  - Emits upload progress callbacks
 *  - Auto-handles expired tokens (401 or invalid token message)
 */
function apiUpload(path, formData, { onProgress } = {}) {
    return new Promise((resolve) => {
        const token = getToken();
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_URL}${path}`, true);
        if (token) {
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }

        if (xhr.upload && typeof onProgress === "function") {
            xhr.upload.onprogress = (event) => {
                const total = Number(event?.total || 0);
                const loaded = Number(event?.loaded || 0);
                const ratio = total > 0 ? loaded / total : 0;
                onProgress({
                    loaded,
                    total,
                    ratio: Number.isFinite(ratio) ? ratio : 0,
                    lengthComputable: Boolean(event?.lengthComputable),
                });
            };
        }

        xhr.onload = () => {
            const status = Number(xhr.status || 0);
            const raw = typeof xhr.responseText === "string" ? xhr.responseText : "";
            let data = {};
            try {
                data = raw ? JSON.parse(raw || "{}") : {};
            } catch {
                data = {};
            }

            if (status === 401 || data?.message === "Invalid or expired token") {
                handleAuthFailure();
                resolve({ ok: false, message: "Invalid or expired token" });
                return;
            }

            resolve(data && typeof data === "object" ? data : {});
        };

        xhr.onerror = () => {
            resolve({ ok: false, message: "Network error" });
        };

        xhr.onabort = () => {
            resolve({ ok: false, message: "Upload cancelled" });
        };

        try {
            xhr.send(formData);
        } catch (err) {
            resolve({ ok: false, message: err?.message || "Upload failed" });
        }
    }).catch((err) => ({ ok: false, message: err?.message || "Upload failed" }));
}

/* ============================================================
   AUTH
============================================================ */

export async function login(email, password) {
    const rememberTwoFactorToken = getRememberedTwoFactorToken();
    return apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({
            email,
            password,
            rememberTwoFactorToken: rememberTwoFactorToken || null,
        }),
    });
}

export async function start2FASetup() {
    return apiFetch("/auth/2fa/setup", {
        method: "POST",
    });
}

export async function verify2FASetup(code) {
    return apiFetch("/auth/2fa/verify-setup", {
        method: "POST",
        body: JSON.stringify({ code }),
    });
}

export async function completeTwoFactorLogin(code, twoFactorToken) {
    return apiFetch("/auth/2fa/login", {
        method: "POST",
        body: JSON.stringify({ code, twoFactorToken }),
    });
}

export async function startPending2FASetup(setupToken, method = "totp") {
    return apiFetch("/auth/2fa/setup-pending", {
        method: "POST",
        body: JSON.stringify({ setupToken, method }),
    });
}

export async function verifyPending2FASetup(setupToken, code, method = "totp") {
    return apiFetch("/auth/2fa/verify-setup-pending", {
        method: "POST",
        body: JSON.stringify({ setupToken, code, method }),
    });
}

export async function resendTwoFactorLoginCode(twoFactorToken) {
    return apiFetch("/auth/2fa/login-resend", {
        method: "POST",
        body: JSON.stringify({ twoFactorToken }),
    });
}

export async function resendPending2FASetupCode(setupToken, method = "email") {
    return apiFetch("/auth/2fa/setup-pending-resend", {
        method: "POST",
        body: JSON.stringify({ setupToken, method }),
    });
}

export async function disable2FA() {
    return apiFetch("/auth/2fa/disable", {
        method: "POST",
    });
}

export async function disableUser2FA(userId) {
    return apiFetch(`/users/${userId}/disable-2fa`, {
        method: "POST",
    });
}

export async function getCurrentUser() {
    return apiFetch("/auth/me");
}

export async function logout() {
    return apiFetch("/auth/logout", {
        method: "POST",
    });
}

export async function forgotPassword(email) {
    const res = await fetch(`${API_URL}/auth/password/forgot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
    });
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getPasswordTokenInfo(token) {
    const qs = new URLSearchParams({ token: String(token || "") });
    const res = await fetch(`${API_URL}/auth/password/token-info?${qs.toString()}`);
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function completePasswordSetup(token, password) {
    const res = await fetch(`${API_URL}/auth/password/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
    });
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

/* ============================================================
   USER MANAGEMENT
============================================================ */

export async function getUsers() {
    const data = await apiFetch("/users");
    if (!data.ok) throw new Error(data.message);
    return data.users;
}

export async function createUser(user) {
    const data = await apiFetch("/users", {
        method: "POST",
        body: JSON.stringify(user),
    });
    if (!data.ok) throw new Error(data.message);
    return data.user;
}

export async function updateUser(id, user) {
    const data = await apiFetch(`/users/${id}`, {
        method: "PUT",
        body: JSON.stringify(user),
    });
    if (!data.ok) throw new Error(data.message);
    return data.user;
}

export async function deleteUser(id) {
    const data = await apiFetch(`/users/${id}`, {
        method: "DELETE",
    });
    if (!data.ok) throw new Error(data.message);
    return true;
}

export async function resendUserPasswordSetup(id) {
    const data = await apiFetch(`/users/${id}/resend-password-setup`, {
        method: "POST",
    });
    if (!data.ok) throw new Error(data.message);
    return data;
}

export async function getUserPodcastPermissions(userId) {
    const data = await apiFetch(`/users/${userId}/podcast-permissions`);
    if (!data.ok) throw new Error(data.message);
    return data.permissions || [];
}

export async function updateUserPodcastPermissions(userId, permissions) {
    const data = await apiFetch(`/users/${userId}/podcast-permissions`, {
        method: "PUT",
        body: JSON.stringify({ permissions }),
    });
    if (!data.ok) throw new Error(data.message);
    return data.permissions || [];
}

/* ============================================================
   CLIENTS
============================================================ */

export async function getClients() {
    const data = await apiFetch("/clients");
    if (!data.ok) throw new Error(data.message);
    return data.clients;
}

export async function createClient(client) {
    const data = await apiFetch("/clients", {
        method: "POST",
        body: JSON.stringify(client),
    });
    if (!data.ok) throw new Error(data.message);
    return data.client;
}

export async function updateClient(id, client) {
    const data = await apiFetch(`/clients/${id}`, {
        method: "PUT",
        body: JSON.stringify(client),
    });
    if (!data.ok) throw new Error(data.message);
    return data.client;
}

export async function deleteClient(id) {
    const data = await apiFetch(`/clients/${id}`, { method: "DELETE" });
    if (!data.ok) throw new Error(data.message);
    return true;
}

/* ============================================================
   LEADS
============================================================ */

export async function getLeads(filters = {}) {
    const qs = new URLSearchParams();

    const search = String(filters.search || "").trim();
    const status = String(filters.status || "").trim();
    const podcastType = String(filters.podcast_type || "").trim();
    const frequency = String(filters.frequency || "").trim();
    const alreadyHasPodcast = String(filters.already_has_podcast || "").trim();

    if (search) qs.set("search", search);
    if (status) qs.set("status", status);
    if (podcastType) qs.set("podcast_type", podcastType);
    if (frequency) qs.set("frequency", frequency);
    if (alreadyHasPodcast) qs.set("already_has_podcast", alreadyHasPodcast);

    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const data = await apiFetch(`/leads${suffix}`);
    if (!data.ok) throw new Error(data.message);
    return data.leads || [];
}

export async function getLeadMetrics(days = 30) {
    const qs = new URLSearchParams({ days: String(days || 30) });
    const data = await apiFetch(`/leads/metrics?${qs.toString()}`);
    if (!data.ok) throw new Error(data.message);
    return data.metrics;
}

export async function createLead(payload) {
    const data = await apiFetch("/leads", {
        method: "POST",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data.lead;
}

export async function updateLead(id, payload) {
    const data = await apiFetch(`/leads/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data.lead;
}

export async function deleteLead(id) {
    const data = await apiFetch(`/leads/${id}`, { method: "DELETE" });
    if (!data.ok) throw new Error(data.message);
    return true;
}

export async function getLeadDiscoverySettings() {
    const data = await apiFetch("/leads/discovery-settings");
    if (!data.ok) throw new Error(data.message);
    return data.settings;
}

export async function updateLeadDiscoverySettings(payload) {
    const data = await apiFetch("/leads/discovery-settings", {
        method: "PUT",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data.settings;
}

export async function getOutlookCalendarsForMailbox(userId) {
    const qs = new URLSearchParams({ user_id: String(userId || "").trim() });
    const data = await apiFetch(`/leads/discovery-settings/calendars?${qs.toString()}`);
    if (!data.ok) throw new Error(data.message);
    return data.calendars || [];
}

export async function resendLeadDiscoveryInvite(leadId) {
    const data = await apiFetch(`/leads/${leadId}/discovery-call/resend`, {
        method: "POST",
    });
    if (!data.ok) throw new Error(data.message);
    return true;
}

export async function getDiscoveryCallPage(token, date = "", debug = false) {
    const qs = new URLSearchParams();
    if (date) qs.set("date", date);
    if (debug) qs.set("debug", "1");
    const suffix = qs.toString() ? `?${qs.toString()}` : "";

    const res = await fetch(
        `${API_URL}/discovery-call/${encodeURIComponent(token)}${suffix}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getDiscoveryCallMonthAvailability(token, month = "") {
    const qs = new URLSearchParams();
    if (month) qs.set("month", month);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await fetch(
        `${API_URL}/discovery-call/${encodeURIComponent(token)}/availability-month${suffix}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function bookDiscoveryCall(token, payload) {
    const res = await fetch(
        `${API_URL}/discovery-call/${encodeURIComponent(token)}/book`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function rescheduleDiscoveryCall(token, payload) {
    const res = await fetch(
        `${API_URL}/discovery-call/${encodeURIComponent(token)}/reschedule`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function cancelDiscoveryCall(token) {
    const res = await fetch(
        `${API_URL}/discovery-call/${encodeURIComponent(token)}/cancel`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getPublicDiscoveryAvailability(date = "") {
    const qs = new URLSearchParams();
    if (date) qs.set("date", date);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await fetch(`${API_URL}/discovery-call/public/availability${suffix}`);
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getPublicDiscoveryMonthAvailability(month = "") {
    const qs = new URLSearchParams();
    if (month) qs.set("month", month);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await fetch(
        `${API_URL}/discovery-call/public/availability-month${suffix}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function createPublicDiscoveryBooking(payload) {
    const res = await fetch(`${API_URL}/discovery-call/public/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getMeetingRequestSettings() {
    const data = await apiFetch("/meeting-requests/settings");
    if (!data.ok) throw new Error(data.message);
    return data.settings;
}

export async function updateMeetingRequestSettings(payload) {
    const data = await apiFetch("/meeting-requests/settings", {
        method: "PUT",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data.settings;
}

export async function getMeetingRequestCalendars(userId) {
    const qs = new URLSearchParams({ user_id: String(userId || "").trim() });
    const data = await apiFetch(`/meeting-requests/settings/calendars?${qs.toString()}`);
    if (!data.ok) throw new Error(data.message);
    return data.calendars || [];
}

export async function getMeetingRequests() {
    const data = await apiFetch("/meeting-requests");
    if (!data.ok) throw new Error(data.message);
    return data.requests || [];
}

export async function createMeetingRequest(payload) {
    const data = await apiFetch("/meeting-requests", {
        method: "POST",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data;
}

export async function getMeetingRequestPage(token, date = "") {
    const qs = new URLSearchParams();
    if (date) qs.set("date", date);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await fetch(
        `${API_URL}/meeting-requests/public/${encodeURIComponent(token)}${suffix}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getMeetingRequestMonthAvailability(token, month = "") {
    const qs = new URLSearchParams();
    if (month) qs.set("month", month);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await fetch(
        `${API_URL}/meeting-requests/public/${encodeURIComponent(token)}/availability-month${suffix}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function bookMeetingRequest(token, payload) {
    const res = await fetch(
        `${API_URL}/meeting-requests/public/${encodeURIComponent(token)}/book`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function rescheduleMeetingRequest(token, payload) {
    const res = await fetch(
        `${API_URL}/meeting-requests/public/${encodeURIComponent(token)}/reschedule`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function cancelMeetingRequest(token) {
    const res = await fetch(
        `${API_URL}/meeting-requests/public/${encodeURIComponent(token)}/cancel`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

/* ============================================================
   PODCASTS
============================================================ */

export async function getPodcasts() {
    return apiFetch("/podcasts");
}

export async function createPodcast(payload) {
    return apiFetch("/podcasts", {
        method: "POST",
        body: JSON.stringify(payload),
    });
}

export async function updatePodcast(id, payload) {
    return apiFetch(`/podcasts/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
    });
}

export async function deletePodcast(id) {
    return apiFetch(`/podcasts/${id}`, {
        method: "DELETE",
    });
}

/* ============================================================
   EPISODES
============================================================ */

export async function getEpisodes() {
    return apiFetch("/episodes");
}

export async function getEpisode(id) {
    return apiFetch(`/episodes/${id}`);
}

export async function createEpisodeShare(episodeId, emails) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/share`, {
        method: "POST",
        body: JSON.stringify({
            emails: emails || "",
        }),
    });
}

export async function sendEpisodeShareEmails(shareToken, emails) {
    return apiFetch(`/episodes/share/${encodeURIComponent(shareToken)}/send-email`, {
        method: "POST",
        body: JSON.stringify({
            emails: emails || "",
        }),
    });
}

export async function getPublicEpisodeShare(token) {
    const res = await fetch(
        `${API_URL}/episodes/share/public/${encodeURIComponent(String(token || ""))}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function listReviewFiles(token, path = "/") {
    const qs = new URLSearchParams({ path: path || "/" });
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(
            String(token || "")
        )}/files?${qs.toString()}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function listReviewMarkers(token) {
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(String(token || ""))}/markers`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function createReviewMarker(token, payload = {}) {
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(String(token || ""))}/markers`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload || {}),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function deleteReviewMarker(token, markerId) {
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(
            String(token || "")
        )}/markers/${encodeURIComponent(String(markerId || ""))}`,
        {
            method: "DELETE",
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function createReviewShare(token, emails) {
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(String(token || ""))}/share`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                emails: emails || "",
            }),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function sendReviewShareEmails(token, shareToken, emails) {
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(
            String(token || "")
        )}/share/${encodeURIComponent(String(shareToken || ""))}/send-email`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                emails: emails || "",
            }),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export function buildReviewMarkerStreamUrl(token) {
    return `${API_URL}/review/${encodeURIComponent(
        String(token || "")
    )}/markers/stream`;
}

export async function getReviewFileStreamUrl(token, itemId) {
    const qs = new URLSearchParams({ itemId: String(itemId || "") });
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(
            String(token || "")
        )}/files/stream-url?${qs.toString()}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export function buildReviewFileStreamProxyUrl(token, itemId) {
    const qs = new URLSearchParams({ itemId: String(itemId || "") });
    return `${API_URL}/review/${encodeURIComponent(
        String(token || "")
    )}/files/stream?${qs.toString()}`;
}

export async function listPublicEpisodeShareFiles(token, path = "/") {
    const qs = new URLSearchParams({ path: path || "/" });
    const res = await fetch(
        `${API_URL}/episodes/share/public/${encodeURIComponent(
            String(token || "")
        )}/files?${qs.toString()}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getPublicEpisodeShareFileStreamUrl(token, itemId) {
    const qs = new URLSearchParams({ itemId: String(itemId || "") });
    const res = await fetch(
        `${API_URL}/episodes/share/public/${encodeURIComponent(
            String(token || "")
        )}/files/stream-url?${qs.toString()}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export function buildPublicEpisodeShareFileStreamProxyUrl(token, itemId) {
    const qs = new URLSearchParams({ itemId: String(itemId || "") });
    return `${API_URL}/episodes/share/public/${encodeURIComponent(
        String(token || "")
    )}/files/stream?${qs.toString()}`;
}

export async function createEpisode(data) {
    return apiFetch("/episodes", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

export async function updateEpisode(id, data) {
    return apiFetch(`/episodes/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

export async function deleteEpisode(id) {
    return apiFetch(`/episodes/${id}`, {
        method: "DELETE",
    });
}

/* ============================================================
   EPISODE CHANGE HISTORY
   GET /episodes/:id/change-logs
============================================================ */

export async function getEpisodeChangeLogs(id) {
    return apiFetch(`/episodes/${id}/change-logs`);
}

export async function getGlobalChangeLogs(filters = {}) {
    const qs = new URLSearchParams();
    const source = String(filters.source || "").trim();
    const action = String(filters.action || "").trim();
    const changedBy = String(filters.changed_by_user_id || "").trim();
    const dateFrom = String(filters.date_from || "").trim();
    const dateTo = String(filters.date_to || "").trim();
    const search = String(filters.search || "").trim();
    const limit = String(filters.limit || "").trim();

    if (source) qs.set("source", source);
    if (action) qs.set("action", action);
    if (changedBy) qs.set("changed_by_user_id", changedBy);
    if (dateFrom) qs.set("date_from", dateFrom);
    if (dateTo) qs.set("date_to", dateTo);
    if (search) qs.set("search", search);
    if (limit) qs.set("limit", limit);

    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch(`/episodes/change-logs/global${suffix}`);
}

/* ============================================================
   MEDIA UPLOADS (FormData)
============================================================ */

export async function uploadEpisodeMedia(id, file, options = {}) {
    const formData = new FormData();
    formData.append("file", file);

    return apiUpload(`/episodes/${id}/media`, formData, options);
}

export async function uploadEpisodeArtwork(id, file, options = {}) {
    const formData = new FormData();
    formData.append("file", file);

    return apiUpload(`/episodes/${id}/artwork`, formData, options);
}

export async function useEpisodeFileAsAudio(id, itemId) {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/media/from-episode-file`, {
        method: "POST",
        body: JSON.stringify({
            item_id: String(itemId || "").trim(),
        }),
    });
}

export async function useEpisodeFileAsArtwork(id, itemId) {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/artwork/from-episode-file`, {
        method: "POST",
        body: JSON.stringify({
            item_id: String(itemId || "").trim(),
        }),
    });
}

export async function transcribeEpisodeAudio(id, itemId = "") {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/transcribe-audio`, {
        method: "POST",
        body: JSON.stringify({
            item_id: String(itemId || "").trim() || null,
        }),
    });
}

export async function processEpisodeAudio(id, itemId = "", presetUuid = "") {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/process-audio`, {
        method: "POST",
        body: JSON.stringify({
            item_id: String(itemId || "").trim() || null,
            preset_uuid: String(presetUuid || "").trim() || null,
        }),
    });
}

export async function processEpisodeMultitrack(id, payload) {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/process-multitrack`, {
        method: "POST",
        body: JSON.stringify(payload || {}),
    });
}

export async function generateEpisodeShowNotes(id) {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/generate-show-notes`, {
        method: "POST",
    });
}

export async function getShowNotesSettings() {
    const data = await apiFetch("/settings/show-notes");
    if (!data.ok) throw new Error(data.message || "Failed to load show notes settings");
    return data.settings;
}

export async function updateShowNotesSettings(payload) {
    const data = await apiFetch("/settings/show-notes", {
        method: "PUT",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message || "Failed to update show notes settings");
    return data.settings;
}

export async function getShowNotesModels(provider = "openai") {
    const resolvedProvider =
        String(provider || "").trim().toLowerCase() === "grok" ? "grok" : "openai";
    const data = await apiFetch(
        `/settings/show-notes/models?provider=${encodeURIComponent(resolvedProvider)}`
    );
    if (!data.ok) throw new Error(data.message || "Failed to load show notes models");
    return Array.isArray(data.models) ? data.models : [];
}

export async function getAuphonicPresets() {
    const data = await apiFetch("/settings/show-notes/auphonic-presets");
    if (!data.ok) throw new Error(data.message || "Failed to load Auphonic presets");
    return Array.isArray(data.presets) ? data.presets : [];
}

// ------------------------------------------------------------
// Backwards compatibility for older code using fetchProtected()
// ------------------------------------------------------------
export async function fetchProtected(url, options = {}) {
    return apiFetch(url, options);
}

/* ============================================================
   EPISODE NOTIFICATIONS
============================================================ */

export async function notifyEpisode(id, message) {
    return apiFetch(`/episodes/${id}/notify`, {
        method: "POST",
        body: JSON.stringify({ message }),
    });
}

/* ============================================================
   GET HEALTH STATUS
============================================================ */

export async function getHealth() {
    try {
        const res = await fetch(`${API_URL}/health`);
        const data = await res.json().catch(() => null);

        if (!res.ok || !data) {
            return { ok: false, status: "down" };
        }

        return data; // { ok: true/false, status: "operational" | "down" }
    } catch (err) {
        console.error("Health API error:", err);
        return { ok: false, status: "down" };
    }
}

/* ============================================================
   SHAREPOINT FILE EXPLORER
============================================================ */

export async function listSharePointItems(path = "/") {
    const qs = new URLSearchParams({ path });
    return apiFetch(`/sharepoint/list?${qs.toString()}`);
}

export async function uploadSharePointFile(path, file, options = {}) {
    const formData = new FormData();
    formData.append("path", path || "/");
    formData.append("file", file);

    return apiUpload("/sharepoint/upload", formData, options);
}

export async function downloadSharePointFile(itemId, requestedFileName = "") {
    const token = getToken();
    const qs = new URLSearchParams({ itemId: String(itemId) });
    if (requestedFileName && String(requestedFileName).trim()) {
        qs.set("fileName", String(requestedFileName).trim());
    }

    const res = await fetch(`${API_URL}/sharepoint/download?${qs.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (res.status === 401) {
        handleAuthFailure();
        return { ok: false, message: "Invalid or expired token" };
    }

    if (!res.ok) {
        const text = await res.text().catch(() => "");
        return {
            ok: false,
            message: text || "Failed to download file",
        };
    }

    const blob = await res.blob();
    const disposition = res.headers.get("content-disposition") || "";
    const encodedMatch = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    const quotedMatch = disposition.match(/filename="([^"]+)"/i);
    const bareMatch = disposition.match(/filename=([^;]+)/i);

    let resolvedFileName = "";
    if (encodedMatch?.[1]) {
        resolvedFileName = decodeURIComponent(encodedMatch[1]).trim();
    } else if (quotedMatch?.[1]) {
        resolvedFileName = quotedMatch[1].trim();
    } else if (bareMatch?.[1]) {
        resolvedFileName = bareMatch[1].trim();
    }

    return { ok: true, blob, fileName: resolvedFileName };
}

export async function getSharePointFileStreamUrl(itemId) {
    const qs = new URLSearchParams({ itemId: String(itemId || "") });
    return apiFetch(`/sharepoint/item-stream-url?${qs.toString()}`);
}

export function buildSharePointFileStreamProxyUrl(itemId) {
    const token = getToken();
    const qs = new URLSearchParams({
        itemId: String(itemId || ""),
        access_token: String(token || ""),
    });
    return `${API_URL}/sharepoint/item-stream?${qs.toString()}`;
}

export async function createSharePointFolder(path, name) {
    return apiFetch("/sharepoint/create-folder", {
        method: "POST",
        body: JSON.stringify({
            path: path || "/",
            name,
        }),
    });
}

export async function deleteSharePointItem(itemId) {
    return apiFetch(`/sharepoint/item/${encodeURIComponent(itemId)}`, {
        method: "DELETE",
    });
}

export async function renameSharePointItem(itemId, newName, targetFolderItemId = "") {
    return apiFetch(`/sharepoint/item/${encodeURIComponent(itemId)}/rename`, {
        method: "PATCH",
        body: JSON.stringify({
            new_name: newName || null,
            target_folder_item_id: targetFolderItemId || null,
        }),
    });
}

export async function setSharePointEpisodeOverride(path, episodeCode) {
    return apiFetch("/sharepoint/episode-override", {
        method: "POST",
        body: JSON.stringify({
            path: path || "/",
            episode_code: episodeCode || "",
        }),
    });
}

export async function clearSharePointEpisodeOverride(path) {
    const qs = new URLSearchParams({ path: path || "/" });
    return apiFetch(`/sharepoint/episode-override?${qs.toString()}`, {
        method: "DELETE",
    });
}

/* ============================================================
   EPISODE SCOPED SHAREPOINT FILE EXPLORER
============================================================ */

export async function listEpisodeFiles(episodeId, path = "/") {
    const qs = new URLSearchParams({ path: path || "/" });
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/files?${qs.toString()}`);
}

export async function uploadEpisodeFile(episodeId, path, file, options = {}) {
    const formData = new FormData();
    formData.append("path", path || "/");
    formData.append("file", file);

    return apiUpload(
        `/episodes/${encodeURIComponent(episodeId)}/files/upload`,
        formData,
        options
    );
}

export async function completeEpisodeFileUploadBatch(episodeId, payload = {}) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/files/upload-complete`, {
        method: "POST",
        body: JSON.stringify(payload || {}),
    });
}

export async function downloadEpisodeFile(
    episodeId,
    itemId,
    requestedFileName = ""
) {
    const token = getToken();
    const qs = new URLSearchParams({ itemId: String(itemId) });
    if (requestedFileName && String(requestedFileName).trim()) {
        qs.set("fileName", String(requestedFileName).trim());
    }

    const res = await fetch(
        `${API_URL}/episodes/${encodeURIComponent(
            episodeId
        )}/files/download?${qs.toString()}`,
        {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
    );

    if (res.status === 401) {
        handleAuthFailure();
        return { ok: false, message: "Invalid or expired token" };
    }

    if (!res.ok) {
        const text = await res.text().catch(() => "");
        return { ok: false, message: text || "Failed to download file" };
    }

    const blob = await res.blob();
    const disposition = res.headers.get("content-disposition") || "";
    const encodedMatch = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    const quotedMatch = disposition.match(/filename="([^"]+)"/i);
    const bareMatch = disposition.match(/filename=([^;]+)/i);

    let resolvedFileName = "";
    if (encodedMatch?.[1]) {
        resolvedFileName = decodeURIComponent(encodedMatch[1]).trim();
    } else if (quotedMatch?.[1]) {
        resolvedFileName = quotedMatch[1].trim();
    } else if (bareMatch?.[1]) {
        resolvedFileName = bareMatch[1].trim();
    }

    return { ok: true, blob, fileName: resolvedFileName };
}

export async function getEpisodeFileStreamUrl(episodeId, itemId) {
    const qs = new URLSearchParams({ itemId: String(itemId || "") });
    return apiFetch(
        `/episodes/${encodeURIComponent(episodeId)}/files/stream-url?${qs.toString()}`
    );
}

export function buildEpisodeFileStreamProxyUrl(episodeId, itemId) {
    const token = getToken();
    const qs = new URLSearchParams({
        itemId: String(itemId || ""),
        access_token: String(token || ""),
    });
    return `${API_URL}/episodes/${encodeURIComponent(episodeId)}/files/stream?${qs.toString()}`;
}

export async function createEpisodeFolder(episodeId, path, name) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/files/create-folder`, {
        method: "POST",
        body: JSON.stringify({
            path: path || "/",
            name,
        }),
    });
}

export async function deleteEpisodeFileItem(episodeId, itemId) {
    return apiFetch(
        `/episodes/${encodeURIComponent(episodeId)}/files/item/${encodeURIComponent(itemId)}`,
        {
            method: "DELETE",
        }
    );
}

export async function renameEpisodeFileItem(
    episodeId,
    itemId,
    newName,
    targetFolderItemId = ""
) {
    return apiFetch(
        `/episodes/${encodeURIComponent(
            episodeId
        )}/files/item/${encodeURIComponent(itemId)}/rename`,
        {
            method: "PATCH",
            body: JSON.stringify({
                new_name: newName || null,
                target_folder_item_id: targetFolderItemId || null,
            }),
        }
    );
}

export async function listEpisodeExternalLinks(episodeId) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/external-links`);
}

export async function createEpisodeExternalLink(episodeId, payload) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/external-links`, {
        method: "POST",
        body: JSON.stringify({
            title: payload?.title ?? "",
            url: payload?.url ?? "",
            notes: payload?.notes ?? "",
        }),
    });
}

export async function updateEpisodeExternalLink(episodeId, linkId, payload) {
    return apiFetch(
        `/episodes/${encodeURIComponent(
            episodeId
        )}/external-links/${encodeURIComponent(linkId)}`,
        {
            method: "PATCH",
            body: JSON.stringify({
                title: payload?.title ?? "",
                url: payload?.url ?? "",
                notes: payload?.notes ?? "",
            }),
        }
    );
}

export async function deleteEpisodeExternalLink(episodeId, linkId) {
    return apiFetch(
        `/episodes/${encodeURIComponent(
            episodeId
        )}/external-links/${encodeURIComponent(linkId)}`,
        {
            method: "DELETE",
        }
    );
}

export async function createEpisodeSharePointFolder(episodeId) {
    return apiFetch(
        `/episodes/${encodeURIComponent(episodeId)}/sharepoint/create-folder`,
        {
            method: "POST",
        }
    );
}
